import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { MapPin, Phone, Globe, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
//const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const { t, language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    preferred_date: '',
  });

  const services = language === 'en' 
    ? ['Nutritional Counselling', 'Endobariatric', 'Bariatric Surgery', 'Metabolic Clinic', 'Diabesity Clinic']
    : ['पोषण परामर्श', 'एंडोबैरिएट्रिक', 'बैरिएट्रिक सर्जरी', 'मेटाबॉलिक क्लीनिक', 'डायबेसिटी क्लीनिक'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`https://bigbariatrics.com/api/send-mail`, formData);
      toast.success(t('form_success'));
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: '',
        preferred_date: '',
      });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="section-padding bg-white"
      data-testid="contact-section"
    >
      <div className="section-container">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <span className="text-[#800000] font-semibold text-sm uppercase tracking-wider">
            {t('contact_subtitle')}
          </span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 p-2"
            data-testid="contact-title"
          >
            {t('contact_title')}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Contact Info & Map */}
          <div>
            {/* Contact Cards */}
            <div className="space-y-4 mb-8">
              {/* Address */}
              <div className="flex items-start gap-4 bg-slate-50 rounded-xl p-4" data-testid="contact-address">
                <div className="w-12 h-12 bg-[#800000] rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">Address</h4>
                  <p className="text-slate-600">{t('contact_address')}</p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start gap-4 bg-slate-50 rounded-xl p-4" data-testid="contact-phones">
                <div className="w-12 h-12 bg-[#800000] rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">Phone</h4>
                  <p className="text-slate-600">
                    <a href="tel:+917554055123" className="hover:text-teal-700">{t('contact_phone1')}</a>
                    <span className="mx-2">|</span>
                    <a href="tel:+919300951215" className="hover:text-teal-700">{t('contact_phone2')}</a>
                  </p>
                </div>
              </div>

              {/* Online Booking */}
              <div className="flex items-start gap-4 bg-slate-50 rounded-xl p-4" data-testid="contact-online">
                <div className="w-12 h-12 bg-[#800000] rounded-xl flex items-center justify-center flex-shrink-0">
                  <Globe className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">{t('contact_online')}</h4>
                  <p className="text-slate-600">{t('contact_online_desc')}</p>
                </div>
              </div>
            </div>

            {/* Google Map */}
            <div className="rounded-xl overflow-hidden shadow-lg" data-testid="google-map">
              <iframe
               src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d6044.557235568076!2d77.437592!3d23.212486!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x397c424dbb83ce07%3A0x98a86ce4c0e19e8d!2sGastrocare%20%2C%20Liver%20%26%20Digestive%20Disease%20Center!5e1!3m2!1sen!2sin!4v1774955279039!5m2!1sen!2sin"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="GastroCare Location"
              ></iframe>

               
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-slate-50 rounded-2xl p-6 md:p-8" data-testid="contact-form-wrapper">
            <form onSubmit={handleSubmit} className="space-y-5 contact-form" data-testid="appointment-form">
              {/* Name */}
              <div>
                <Label htmlFor="name" className="text-slate-700 font-medium">{t('form_name')}</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="mt-1.5"
                  placeholder={language === 'en' ? 'Enter your full name' : 'अपना पूरा नाम दर्ज करें'}
                  data-testid="form-name-input"
                />
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email" className="text-slate-700 font-medium">{t('form_email')}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="mt-1.5"
                  placeholder={language === 'en' ? 'Enter your email' : 'अपना ईमेल दर्ज करें'}
                  data-testid="form-email-input"
                />
              </div>

              {/* Phone */}
              <div>
                <Label htmlFor="phone" className="text-slate-700 font-medium">{t('form_phone')}</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="mt-1.5"
                  placeholder="+91-XXXXXXXXXX"
                  data-testid="form-phone-input"
                />
              </div>

              {/* Service Selection */}
              <div>
                <Label htmlFor="service" className="text-slate-700 font-medium">{t('form_service')}</Label>
                <Select onValueChange={handleServiceChange} value={formData.service}>
                  <SelectTrigger className="mt-1.5" data-testid="form-service-select">
                    <SelectValue placeholder={language === 'en' ? 'Select a service' : 'सेवा चुनें'} />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((service, index) => (
                      <SelectItem key={index} value={service} data-testid={`service-option-${index}`}>
                        {service}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Preferred Date */}
              <div>
                <Label htmlFor="preferred_date" className="text-slate-700 font-medium">{t('form_date')}</Label>
                <Input
                  id="preferred_date"
                  name="preferred_date"
                  type="date"
                  value={formData.preferred_date}
                  onChange={handleChange}
                  className="mt-1.5"
                  data-testid="form-date-input"
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="message" className="text-slate-700 font-medium">{t('form_message')}</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="mt-1.5"
                  rows={4}
                  placeholder={language === 'en' ? 'Tell us about your concern...' : 'अपनी चिंता के बारे में बताएं...'}
                  data-testid="form-message-input"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="form-submit-button"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5" />
                    <span>{t('form_submit')}</span>
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
